import './SectionBreakdownList.scss'

import classNames from 'classnames'
import { config } from 'lib/config'
import React from 'react'
import { formatLocalTimeDuration } from 'utils/formatters'

import DelayTime from '../DelayTime'

export enum LineType {
  Dotted = 'dotted',
  Solid = 'solid',
  BulletPoint = 'bullet-point',
}

export interface SectionBreakdownItem {
  title: string
  distance?: number
  icon?: string
  departure?: Date
}

export interface LineOptions {
  type: LineType
  color: string
}

export interface SectionBreakdownListProps {
  title: string
  delay?: number
  duration?: number
  line: LineOptions
  breakdownItems: SectionBreakdownItem[]
  isCollapsed?: boolean
  onToggle?: (toggle: boolean) => void
  hasPreview?: boolean
  className?: string
}

export default function SectionBreakdownList({
  title,
  delay,
  duration,
  line,
  breakdownItems,
  isCollapsed,
  onToggle,
  hasPreview,
  className,
}: SectionBreakdownListProps): JSX.Element {
  const isCollapsible = onToggle !== undefined && isCollapsed !== undefined
  const cssBorderType = 'solid'
  const previewItem = breakdownItems[breakdownItems.length - 1]
  const previewTitle = previewItem !== undefined ? previewItem.title : ''

  return (
    <div
      className={classNames('SectionBreakdownList', className, {
        'SectionBreakdownList--expanded': isCollapsible ? isCollapsed : true,
      })}
      style={
        {
          '--border-color-type': cssBorderType,
          '--border-color-var': line.color,
        } as React.CSSProperties
      }
    >
      <header
        className={classNames('SectionBreakdownList__header', {
          'SectionBreakdownList__header--cursor-pointer': isCollapsible,
        })}
        onClick={() => isCollapsible && onToggle(!isCollapsed)}
      >
        {isCollapsible ? (
          <img
            className="SectionBreakdownList__chevron-icon"
            src={`${config.ROOT_PATH}images/chevron-down.svg`}
          />
        ) : null}
        <div className="SectionBreakdownList__title">
          <span className="SectionBreakdownList__title-label">{title}</span>
          {duration !== undefined && duration !== 0 ? (
            <>
              <span className="SectionBreakdownList__title-label">•</span>
              <span className="SectionBreakdownList__title-label">
                {formatLocalTimeDuration(duration)}
              </span>
            </>
          ) : null}
        </div>
      </header>

      <div className="SectionBreakdownList__content">
        {breakdownItems?.map((element, index) => (
          <div
            key={index}
            className={classNames('SectionBreakdownList__item', {
              'SectionBreakdownList--bullet-point': line.type === LineType.BulletPoint,
            })}
          >
            {element.icon !== undefined ? (
              <svg className="SectionBreakdownList__icon">
                <use href={`#${element.icon}`} fill="var(--hds-foreground-secondary)" />
              </svg>
            ) : null}
            <div className="SectionBreakdownList__details">
              <div className="SectionBreakdownList__heading-line">
                <span className="SectionBreakdownList__title">{element.title}</span>
                {element.departure && duration !== undefined ? (
                  <DelayTime time={element.departure} delay={delay} duration={duration} />
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
      {hasPreview !== undefined && hasPreview && isCollapsed !== undefined && !isCollapsed && (
        <div className="SectionBreakdownList__preview">
          <span>{previewTitle}</span>
          {previewItem?.departure !== undefined && duration !== undefined ? (
            <DelayTime time={previewItem.departure} duration={duration} delay={delay} />
          ) : null}
        </div>
      )}
    </div>
  )
}
